import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DISASSEMBLY_LEVELS, priceWrapLine } from '@wrapforce/shared';
import type { DisassemblyLevel, WrapPricingInput } from '@wrapforce/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { useLocaleFormatters } from '@/lib/format';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { usePutVehicleDisassemblyOverride } from '@/features/settings/pricing-v2/api';
import { useUpdateWorkspaceVehicle } from './pricingApi';
import type { VehicleSelection } from './VehiclePicker';

// Sibling of settings/pricing-v2/VehiclesTab.tsx's own local HoursField -
// small enough, and each only used within its own file, to duplicate rather
// than share across a settings-page component and a mid-quote one (D-34-05's
// same "settings-only forms don't reuse quote-building components" rationale,
// applied in reverse here).
function HoursField({
  id, label, minutes, onChange,
}: {
  id: string;
  label: string;
  minutes: number;
  onChange: (minutes: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        max={100}
        step={0.25}
        value={minutes / 60}
        onChange={(e) => onChange(Math.max(0, Math.round(Number(e.target.value) * 60)))}
      />
    </div>
  );
}

export function DisassemblySelector({
  workspaceId, vehicle, baseInput, disassemblyLevel, onChangeLevel, doorJambs, onChangeDoorJambs, clipBreakageAck, onChangeClipBreakageAck,
  adhesionRisk, onChangeAdhesionRisk, onVehicleMinutesSaved,
}: {
  workspaceId: string | null;
  vehicle: VehicleSelection;
  baseInput: WrapPricingInput;
  disassemblyLevel: DisassemblyLevel;
  onChangeLevel: (level: DisassemblyLevel) => void;
  doorJambs: boolean;
  onChangeDoorJambs: (v: boolean) => void;
  clipBreakageAck: boolean;
  onChangeClipBreakageAck: (v: boolean) => void;
  adhesionRisk: boolean;
  onChangeAdhesionRisk: (v: boolean) => void;
  onVehicleMinutesSaved: (l1Minutes: number, l2Minutes: number, l3Minutes: number) => void;
}) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  const l0Total = priceWrapLine({ ...baseInput, disassemblyLevel: 'L0', doorJambs: false }).lineSubtotalCents;

  // Same OM-only gate the backend enforces on the override PUT route (0040's
  // RLS) - hidden rather than shown-then-403'd for front_desk, who can build
  // quotes (OMF) but can't edit shop-wide vehicle pricing.
  const { membership } = useActiveMembership();
  const canEditVehiclePricing = membership?.role === 'owner' || membership?.role === 'manager';

  const queryClient = useQueryClient();
  const updateWorkspaceVehicle = useUpdateWorkspaceVehicle(workspaceId);
  const putOverride = usePutVehicleDisassemblyOverride(workspaceId);
  const savingVehicleMinutes = updateWorkspaceVehicle.isPending || putOverride.isPending;

  const [editTimesOpen, setEditTimesOpen] = useState(false);
  const [l1Minutes, setL1Minutes] = useState(vehicle.l1DisassemblyMinutes);
  const [l2Minutes, setL2Minutes] = useState(vehicle.l2DisassemblyMinutes);
  const [l3Minutes, setL3Minutes] = useState(vehicle.l3DisassemblyMinutes);

  function openEditTimes() {
    setL1Minutes(vehicle.l1DisassemblyMinutes);
    setL2Minutes(vehicle.l2DisassemblyMinutes);
    setL3Minutes(vehicle.l3DisassemblyMinutes);
    setEditTimesOpen(true);
  }

  // Writes through to the same per-vehicle pricing data Settings -> Pricing ->
  // Vehicles manages (workspace-custom vehicle -> PATCH /pricing-vehicles,
  // catalog vehicle -> PUT /vehicle-disassembly-overrides) - this is a shop-
  // wide default, not a one-off for this line, so every quote that picks this
  // vehicle afterward sees the corrected time too.
  async function handleSaveVehicleMinutes() {
    try {
      if (vehicle.workspaceVehicleId) {
        await updateWorkspaceVehicle.mutateAsync({
          vehicleId: vehicle.workspaceVehicleId,
          input: { l1DisassemblyMinutes: l1Minutes, l2DisassemblyMinutes: l2Minutes, l3DisassemblyMinutes: l3Minutes },
        });
      } else if (vehicle.catalogVehicleId) {
        await putOverride.mutateAsync({
          vehicleId: vehicle.catalogVehicleId,
          input: { l1Minutes, l2Minutes, l3Minutes },
        });
      } else {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicles-panel'] }),
        queryClient.invalidateQueries({ queryKey: [workspaceId, 'vehicle-catalog'] }),
        queryClient.invalidateQueries({ queryKey: [workspaceId, 'pricing-vehicles'] }),
      ]);
      onVehicleMinutesSaved(l1Minutes, l2Minutes, l3Minutes);
      toast.success(t('pricing.disassembly.saveToast'));
      setEditTimesOpen(false);
    } catch {
      toast.error(t('pricing.disassembly.saveError'));
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {DISASSEMBLY_LEVELS.map((level) => {
          const totalAtLevel = priceWrapLine({ ...baseInput, disassemblyLevel: level, doorJambs: level === disassemblyLevel ? doorJambs : false }).lineSubtotalCents;
          const deltaCents = totalAtLevel - l0Total;
          const isSelected = disassemblyLevel === level;
          return (
            <button
              key={level}
              type="button"
              onClick={() => onChangeLevel(level)}
              className={`flex flex-col gap-1 border p-3 text-left ${isSelected ? 'border-accent bg-accent/10' : 'border-border hover:bg-surface-2'}`}
            >
              <span className="text-sm font-bold">{t(`pricing.disassembly.level.${level}.title`)}</span>
              <span className="text-xs text-muted">{t(`pricing.disassembly.level.${level}.body`)}</span>
              <span className="mt-1 font-mono text-xs font-semibold text-accent-ink">
                {deltaCents === 0 ? t('pricing.disassembly.baseline') : `${deltaCents > 0 ? '+' : ''}${formatMoney(deltaCents)} ${t('pricing.disassembly.vsL0')}`}
              </span>
            </button>
          );
        })}
      </div>

      {disassemblyLevel === 'L2' || disassemblyLevel === 'L3' ? (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={doorJambs} onChange={(e) => onChangeDoorJambs(e.target.checked)} />
          <span>{t('pricing.disassembly.doorJambsLabel')}</span>
        </label>
      ) : null}

      {disassemblyLevel !== 'L0' ? (
        <div className="flex flex-col gap-2 border border-border bg-surface-2 p-3">
          <p className="text-xs text-muted">{t('pricing.disassembly.clipBreakageNote')}</p>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={clipBreakageAck} onChange={(e) => onChangeClipBreakageAck(e.target.checked)} />
            <span>{t('pricing.disassembly.clipBreakageAck')}</span>
          </label>
        </div>
      ) : null}

      {disassemblyLevel === 'L2' || disassemblyLevel === 'L3' ? (
        <div className="flex flex-col gap-2 border border-border bg-surface-2 p-3">
          <Label>{t('pricing.disassembly.adhesionRiskLabel')}</Label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={adhesionRisk} onChange={(e) => onChangeAdhesionRisk(e.target.checked)} />
            <span>{t('pricing.disassembly.adhesionRiskAck')}</span>
          </label>
        </div>
      ) : null}

      {canEditVehiclePricing ? (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => (editTimesOpen ? setEditTimesOpen(false) : openEditTimes())}
            className="w-fit text-xs font-semibold text-accent-ink hover:underline"
          >
            {editTimesOpen ? t('pricing.cancelEdit') : t('pricing.disassembly.editTimesToggle')}
          </button>
          {editTimesOpen ? (
            <div className="flex flex-col gap-2.5 border border-dashed border-border p-3">
              <p className="text-xs text-muted">{t('pricing.disassembly.editTimesNote')}</p>
              <div className="grid grid-cols-3 gap-2.5">
                <HoursField id="disassembly-edit-l1" label={t('pricing.disassembly.level.L1.title')} minutes={l1Minutes} onChange={setL1Minutes} />
                <HoursField id="disassembly-edit-l2" label={t('pricing.disassembly.level.L2.title')} minutes={l2Minutes} onChange={setL2Minutes} />
                <HoursField id="disassembly-edit-l3" label={t('pricing.disassembly.level.L3.title')} minutes={l3Minutes} onChange={setL3Minutes} />
              </div>
              <Button type="button" size="sm" loading={savingVehicleMinutes} onClick={handleSaveVehicleMinutes} className="w-fit">
                {t('pricing.disassembly.saveToVehicle')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
